import {
	H2,
	HStack,
	VStack,
	Text,
	Box,
	Button,
} from '@montel/montelpro-shared-components-v2';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { ADMIN_ACCOUNT_API_ROUTE, ADMIN_ACCOUNT_DELETE } from 'apiRoutes';
import SectionDivider from 'components/common/SectionDivider';
import { FunctionComponent, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Roles } from './Roles';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import styled from 'styled-components';
import { ResetPasswordModal } from './ResetPasswordModal';
import { getAdminNameOrNotAvailable } from '../AdminAccountsPage/AdminAccountTable/adminAccountTableColumns';
import { LabelAndValue } from 'components/common/LabelAndValue/LabelAndValue';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { AdminRole } from 'domain/account/enums/AdminRole';
import BackButton from 'components/common/BackButton/BackButton';
import { AdminAuditLog } from './AdminAuditLog';
import { RequestLoader } from 'components/common/Loader';
import { ConfirmationModal } from 'components/common/ConfirmationModal/ConfirmationModal';
import useAuthRoles from 'hooks/useAuthRoles';
import useSubmit, { POST } from 'hooks/useSubmit';
import { AdminAccount } from 'domain/account/interfaces';

const PasswordResetLink = styled(Text)`
	cursor: pointer;
`;

export const AdminAccountProfilePage: FunctionComponent = () => {
	const { id } = useParams();
	const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
	const [passwordWasReset, setPasswordWasReset] = useState(false);
	const { data: user, status: getUserStatus } = useGetOnMount<AdminAccount>(
		`${ADMIN_ACCOUNT_API_ROUTE}/GetAdmin?id=${id}`,
	);

	const { isSuperAdmin } = useAuthRoles();
	const navigate = useNavigate();

	const [deleteAdmin, deleteAdminStatus] = useSubmit(
		ADMIN_ACCOUNT_DELETE,
		POST,
		{
			messageOnSuccess: 'Admin successfully deleted.',
			messageOnError: 'An error occured, please try again.',
			showApiError: true,
		},
	);

	const [showConfirmationModal, setShowConfirmationModal] = useState(false);

	return (
		<RequestLoader h="500px" status={getUserStatus}>
			{user && (
				<Box bg="bgMainContainer">
					<BackButton />
					<HStack justify="space-between" align="center" fullWidth>
						<H2>{getAdminNameOrNotAvailable(user)}</H2>
						{isSuperAdmin && (
							<Button
								variant="negative"
								text={'Delete admin'}
								onClick={() => setShowConfirmationModal(true)}
							/>
						)}
					</HStack>
					<SectionDivider />
					<VStack gap="xl" fullWidth>
						<HStack gap="md" fullWidth>
							<LabelAndValue
								label="Email"
								value={user?.email}
								minWidth="100%"
							/>
							<LabelAndValue
								label="Registered"
								value={toDefaultDateFormatOrNotAvailable(
									user?.registered,
								)}
								minWidth="100%"
							/>
						</HStack>
						<Roles user={user} />
						{passwordWasReset ? (
							<Box w="380px">
								<Text>
									User&apos;s password has been reset. They
									will need to select a new password the next
									time they login.
								</Text>
							</Box>
						) : (
							<AccessControl
								allowedRoles={[AdminRole.SUPER_ADMIN]}
							>
								<PasswordResetLink
									size="md"
									color="pineGreen"
									decoration="underline"
									onClick={() =>
										setShowPasswordResetModal(true)
									}
								>
									Reset password
								</PasswordResetLink>
							</AccessControl>
						)}
						<SectionDivider />
						<AdminAuditLog adminId={user.id} />
					</VStack>
					<ResetPasswordModal
						showModal={showPasswordResetModal}
						setShowModal={setShowPasswordResetModal}
						onSuccess={() => setPasswordWasReset(true)}
						user={user}
					/>
				</Box>
			)}
			<ConfirmationModal
				showModal={showConfirmationModal}
				setShowModal={setShowConfirmationModal}
				onSubmit={async (comment) => {
					const result = await deleteAdmin({
						adminId: user.id,
						comment: comment,
						eventData: {
							email: user.email,
						},
					});

					if (result.success) {
						navigate('/accounts/admin-users');
					}
				}}
				header={'Delete admin account'}
				text={`Are you sure you want to delete admin ${getAdminNameOrNotAvailable(
					user,
				)}?`}
				buttonText={'Delete'}
				loadingStatus={deleteAdminStatus}
				loadingText="Deleting..."
			/>
		</RequestLoader>
	);
};
